import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

type MessageBody = {
	body?: string;
};

/**
 * Extracts the message body from a post message.
 * Handles both plain text messages and JSON-formatted messages.
 * @param message - The post message (can be null, plain text, or JSON string)
 * @returns The extracted message text or empty string
 */
function extractMessage(message: string | null): string {
	if (!message) return '';

	try {
		const parsed = JSON.parse(message) as MessageBody;
		if ('body' in parsed) {
			return parsed.body || '';
		}
	} catch {
		// Not JSON, use message as-is
	}

	return message;
}

/**
 * Validates if a post has all required content to be displayed.
 * @param post - The Ready to Share post
 * @param isStory - Whether this is a story (true) or regular post (false)
 * @returns true if the post has valid content, false otherwise
 *
 * Validation rules:
 * - Regular posts: Must have BOTH a valid message AND at least one non-processing image
 * - Stories: Must have at least one non-processing image (message is optional)
 */
export function hasValidContent(post: ReadyToSharePostWithPlatform, isStory: boolean): boolean {
	// Check for valid, non-processing images
	const hasValidImages = post.images.length > 0 && post.images.some((img) => !img.processing);

	// Stories only need valid images
	if (isStory) {
		return hasValidImages;
	}

	// Regular posts need both message and images
	const message = extractMessage(post.message);
	const hasValidMessage = message.length > 0;

	return hasValidMessage && hasValidImages;
}
